<template>
  <div v-if="collectionLoaded">
    <div
      v-if="displayHeader()"
      ref="grid"
      class="Collection-Header page-width"
      data-color="night-sky-dark"
    >
      <template v-if="displayCollectionTitle()">
        <h4 v-if="altTitleFont" class="t1" v-html="title"></h4>
        <h4
          v-else
          :class="[
            { h4: template == 'collection' || 'page', h2: template == 'index' },
            'Collection-Title',
          ]"
          v-html="title"
        ></h4>
      </template>
      <p
        v-if="description !== ''"
        class="p7 Collection-Description"
        v-html="description"
      ></p>
    </div>
    <template v-else>
      <span ref="grid" class="page-width Collection-Header-Blank"></span>
    </template>
    <ul
      v-if="items"
      :key="instance"
      :class="['Collection-Grid', collectionGridClasses(items.length)]"
    >
      <li
        v-for="(item, index) in items"
        :key="item"
        :data-index="index + 1"
        :data-colspan="item.content?.colspan"
        :data-end-col="updateRowPosition(item.content?.colspan)"
        class="Collection-Grid__Item"
      >
        <ContentTile
          v-if="item.cardType === 'CONTENT'"
          :content="item.content"
        />
        <ProductCard
          v-else
          :item="item"
          :ready="promoReady"
          :terms="terms"
          :selection-listener="selectionListener"
        />
      </li>
      <li ref="bottomOfGrid" class="bottomOfGrid"></li>
    </ul>
    <div
      v-if="count && length !== count && count < length"
      class="page-width Collection-LoadMore"
    >
      <button
        v-if="loadMore"
        class="button button--secondary"
        @click="loadAllItems()"
      >
        {{ loadLabel }}
      </button>
      <a
        v-else-if="shopBtn"
        :href="shopBtn"
        class="button button--secondary"
        v-html="shopBtnLabel"
      ></a>
    </div>
  </div>

  <div v-else ref="bottomOfGrid" class="empty bottomOfGrid"></div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue';
import ContentTile from '../Global/ContentTile.vue';
import ProductCard from '../Global/ProductCard.vue';
import { FetchCards } from './Composables/FetchCards';
import { pubSub } from '@utils/PubSub';

const items = ref(null);
const title = ref(null);
const anchor = ref(null);
const template = ref(null);
const custom = ref(null);
const description = ref(null);
const altTitleFont = ref(null);
const paginationInterval = ref(false);
const bottomOfGrid = ref(null);
const allItemsLoaded = ref(false);
const collectionLoaded = ref(false);
const promoReady = ref(false);
const grid = ref(null);
const loadMore = ref(false);
const count = ref(false);
const length = ref(null);
const shopBtnLabel = ref(null);
const shopBtn = ref(null);
const loadLabel = ref(null);
const instance = ref(0);

const props = defineProps({
  data: { type: Object, required: false, default: () => ({}) },
  paginate: { type: [String, Boolean], default: 'false' },
  selectionListener: { type: Boolean, default: false },
  terms: { type: Object, required: false, default: () => ({}) },
});

let rowPosition = 0;
const updateRowPosition = (colspan) => {
  const columns = parseInt(colspan || 1);
  rowPosition += columns;
  return rowPosition % 3;
};

const displayCollectionTitle = () => title.value !== '';

const displayHeader = () => {
  const templateType =
      template.value == 'collection' ||
      template.value == 'index' ||
      template.value == 'page',
    hideCollectionHeader =
      custom.value.collectiondata.hideCollectionHeader.includes('true'),
    hasDesc = description.value !== '',
    hideItems = !hideCollectionHeader || hasDesc;
  return items.value && templateType && hideItems;
};

const collectionGridClasses = (num) => {
  const loadMoreEnabled = loadMore.value || shopBtn.value;
  const isRowFull = num % 3 !== 0;
  const isMobileRowFull = num % 2 == 1;
  let value = '';

  if (loadMoreEnabled && template.value == 'collection') {
    if (!isRowFull) value = 'grid--padded-none';
    if (!isMobileRowFull) value = value + ' grid--padded-none-mobile';
  } else {
    if (isRowFull) value = 'grid--padded';
    if (isMobileRowFull) value = value + ' grid--padded-mobile';
  }

  return value;
};

const setItems = async (context) => {
  const alreadyLoaded = collectionLoaded.value || false;
  const { items: data } = await FetchCards(context);
  items.value = alreadyLoaded ? [...items.value, ...data] : data;
  allItemsLoaded.value = items.value.length >= props.data.items.length;
  collectionLoaded.value = true;
};

const updateItemList = async () => {
  const index = parseInt(paginationInterval.value) || false;
  if (!index || allItemsLoaded.value) return;
  paginationInterval.value = index + parseInt(props.paginate);
  const updatedItems = props.data.items.slice(index, paginationInterval.value);
  return setItems(updatedItems);
};

const loadItems = () => {
  if (props.data.items.length <= 0) return;

  length.value = props.data.items.length;
  count.value =
    props.data.custom.collectiondata.grid.count != 'false'
      ? parseInt(props.data.custom.collectiondata.grid.count)
      : length.value;
  const usePagination = props.paginate !== 'false';
  const dataItems = usePagination ? parseInt(props.paginate) : count.value;
  if (length.value !== count.value && count.value < length.value) {
    loadLabel.value = props.data.custom.collectiondata.grid.label;
    loadMore.value = !loadMore.value;
  }
  const items = props.data.items.slice(0, dataItems);
  title.value = props.data.title;
  anchor.value = props.data.handle;
  template.value = props.data.template;
  custom.value = props.data.custom;
  description.value = custom.value.collectiondata.description;
  altTitleFont.value = custom.value.collectiondata.altTitleFont === 'true';

  if (!items) return;
  return setItems(items);
};

const loadAllItems = () => {
  shopBtn.value =
    props.data.custom.collectiondata.grid.shopButton.link != 'false'
      ? props.data.custom.collectiondata.grid.shopButton.link
      : false;
  shopBtnLabel.value =
    props.data.custom.collectiondata.grid.shopButton.label != 'false'
      ? props.data.custom.collectiondata.grid.shopButton.label
      : `Shop More`;

  const remainingItems = props.data.items.slice(count.value, length.value);
  setItems(remainingItems);
  setTimeout(() => (loadMore.value = !loadMore.value), 250);
};

const scrollToGrid = () => {
  const hash = location?.hash;
  if (hash) {
    const strippedHash = hash.replace('#', '');

    if (strippedHash === anchor.value) {
      grid.value.scrollIntoView();
    }
  }
};

const onIntersection = (entries) => {
  const isIntersecting = entries[0]?.isIntersecting || false;
  if (isIntersecting) updateItemList();
};

const removeStencils = () => {
  const stencils = document.querySelectorAll('.js-collection-stencils');
  if (stencils.length <= 0) return;

  stencils.forEach((stencil) => stencil.remove());
};

const observeScroll = () => {
  removeStencils();
  if (!paginationInterval.value) return;
  const observer = new IntersectionObserver(onIntersection, {
    rootMargin: `100px 0px 0px 0px`,
  });
  observer.observe(bottomOfGrid.value);
};

const mountPromo = () => {
  pubSub.unsubscribe('dy:promo:init', mountPromo);
  pubSub.publish('dy:promo:ready');
};

const mountFallback = () => {
  pubSub.publish('dy:promo:ready');
};

const setPromo = () => {
  promoReady.value = true;
};

const updateProps = ({ detail }) => {
  items.value = [];
  props.data.items = detail; // eslint-disable-line
  loadItems();
  instance.value = instance.value + 1;
};

pubSub.subscribe('swym:results:update', updateProps);
pubSub.subscribe('dy:promo:init', mountPromo, mountFallback);
pubSub.subscribe('dy:promo:ready', setPromo);

onMounted(() => {
  loadItems();
});

watch(collectionLoaded, observeScroll);

watch(grid, () => {
  setTimeout(scrollToGrid, 1000);
});
</script>
